import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/apiClient";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = logged out
  const [memberships, setMemberships] = useState([]);
  const [activeOrgId, setActiveOrgId] = useState(localStorage.getItem("pitso_org") || null);
  const [loading, setLoading] = useState(true);

  const applyMemberships = useCallback((mships) => {
    setMemberships(mships || []);
    const stored = localStorage.getItem("pitso_org");
    const valid = (mships || []).some((m) => m.org_id === stored);
    if (!valid && mships && mships.length) {
      localStorage.setItem("pitso_org", mships[0].org_id);
      setActiveOrgId(mships[0].org_id);
    } else if (valid) {
      setActiveOrgId(stored);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data.user);
        applyMemberships(data.memberships);
      } catch {
        setUser(false);
      } finally {
        setLoading(false);
      }
    })();
  }, [applyMemberships]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("pitso_token", data.access_token);
    setUser(data.user);
    applyMemberships(data.memberships);
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("pitso_token", data.access_token);
    setUser(data.user);
    applyMemberships(data.memberships);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("pitso_token");
    localStorage.removeItem("pitso_org");
    setUser(false);
    setMemberships([]);
    setActiveOrgId(null);
  };

  const switchOrg = (orgId) => {
    localStorage.setItem("pitso_org", orgId);
    setActiveOrgId(orgId);
  };

  const refreshMe = async () => {
    const { data } = await api.get("/auth/me");
    setUser(data.user);
    applyMemberships(data.memberships);
  };

  const activeMembership = memberships.find((m) => m.org_id === activeOrgId) || null;
  const role = user?.is_super_admin ? "super_admin" : activeMembership?.role || null;

  return (
    <AuthContext.Provider value={{
      user, loading, memberships, activeOrgId, activeMembership, role,
      login, register, logout, switchOrg, refreshMe,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

const RANK = { viewer: 1, moderator: 2, org_admin: 3, org_owner: 4, super_admin: 5 };
export function hasRole(role, min) {
  return (RANK[role] || 0) >= (RANK[min] || 99);
}
